import { relativeTime } from '../../shared/lib/format';
import { errorClassMeta } from '../../shared/status/dictionary';
import type { TDestination, TDestinationConfig, TProbe, TProbePhase } from './api';

export type TDestinationState = TDestination['state'];
export type TDestinationActivity = TDestination['activity'];
export type TAuthType = NonNullable<TDestinationConfig['auth']>['type'];
export type THttpMethod = NonNullable<TDestinationConfig['method']>;

// `computedAt: null` means the rollup has not run — never print it as `0`.
export const counted = (value: number, computedAt: string | null): string | null =>
  computedAt === null ? null : value.toLocaleString('en-US');

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

export const usagePhrase = (activity: TDestinationActivity): string =>
  activity.flowsUsing === 0
    ? 'Nothing sends here'
    : `Used by ${plural(activity.flowsUsing, 'flow')}`;

/** An absent `activity` means the server is older than this bundle. */
export const destinationActivityLine = (
  activity: TDestinationActivity | undefined,
  now: number,
): string => {
  if (!activity) return 'Traffic has not been counted yet';

  const parts = [usagePhrase(activity)];
  if (activity.lastSuccessAt !== null || activity.computedAt !== null) {
    parts.push(`last delivered ${relativeTime(activity.lastSuccessAt, now)}`);
  }

  const delivered = counted(activity.deliveredInWindow, activity.computedAt);
  const window = `the past ${activity.windowHours} hours`;
  parts.push(
    delivered === null ? `${window} not counted yet` : `${delivered} delivered in ${window}`,
  );
  return parts.join(' · ');
};

/** "502" or "Connection refused" — the last thing it said, in the Events vocabulary. */
export const lastAnswer = (activity: TDestinationActivity | undefined): string | null => {
  if (!activity) return null;
  const errorClass = activity.lastErrorClass;
  const failedLast =
    activity.lastFailureAt !== null &&
    (activity.lastSuccessAt === null || activity.lastFailureAt > activity.lastSuccessAt);

  if (failedLast && errorClass && !errorClass.startsWith('status_')) {
    return errorClassMeta(errorClass)?.label ?? errorClass;
  }
  if (activity.lastResponseStatus !== null) return String(activity.lastResponseStatus);
  return errorClass ? (errorClassMeta(errorClass)?.label ?? errorClass) : null;
};

export const endpointLabel = (destination: TDestination): string => {
  if (destination.transport === 'cli') {
    return `CLI · ${destination.config.label?.trim() || destination.name}`;
  }
  return destination.config.url ?? destination.transport;
};

/** The server's bounds on `timeoutMs`, in the seconds the form speaks. */
export const TIMEOUT_SECONDS = { min: 1, max: 900 } as const;

export const timeoutSeconds = (ms: number): string => String(Number((ms / 1000).toFixed(3)));

export const toTimeoutMs = (seconds: string): number => Math.round(Number(seconds) * 1000);

export const duration = (ms: number | null): string => {
  if (ms === null) return '—';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
};

const PHASE_LABEL: Record<TProbePhase, string> = {
  dns: 'dns lookup',
  tcp: 'tcp connect',
  tls: 'tls handshake',
  response: 'the reply',
  connect: 'connect',
  check: 'the check',
  unavailable: 'the transport',
};

export const phaseLabel = (phase: TProbePhase): string => PHASE_LABEL[phase] ?? phase;

export interface IMeasuredPhase {
  name: string;
  ms: number;
}

// A null leg means the phase never ran; it is dropped, never drawn as 0 ms.
export const measuredPhases = (probe: TProbe): IMeasuredPhase[] => {
  const timings = probe.timings;
  if (!timings) return [];

  const legs: [string, number | null][] = [
    ['dns', timings.dnsMs],
    ['tcp', timings.tcpMs],
    ['tls', timings.tlsMs],
  ];

  return legs
    .filter((leg): leg is [string, number] => leg[1] !== null)
    .map(([name, ms]) => ({ name, ms }));
};

export const headerEntries = (headers: Record<string, string> | undefined): [string, string][] =>
  Object.entries(headers ?? {});

export const rateLimitPhrase = (perSec: number | null, burst: number | null): string => {
  if (perSec === null) return 'no limit';
  return burst === null ? `${perSec} / second` : `${perSec} / second, bursts of ${burst}`;
};

export const SIGNATURE_HEADER = 'x-hookpipe-signature';

const HOP_BY_HOP = new Set([
  'host',
  'content-length',
  'connection',
  'keep-alive',
  'transfer-encoding',
  'upgrade',
  'te',
  'expect',
  'proxy-authorization',
]);

interface IAuthShape {
  type: TAuthType;
  header?: string;
}

// Mirrors `reservedHeaderReason` on the server so the form says no before Save does.
export const reservedHeaderReason = (name: string, auth: IAuthShape | undefined): string | null => {
  const key = name.trim().toLowerCase();
  if (!key) return null;
  if (HOP_BY_HOP.has(key)) return `${key} is set by the HTTP client, per request`;
  if (key === 'idempotency-key') return 'idempotency-key is the event id; receivers dedupe on it';
  if (key === SIGNATURE_HEADER) return `${key} carries our signature`;
  if (key.startsWith('x-hookpipe-')) return `${key} is one of hookpipe's own headers`;
  if (key === 'authorization' && (auth?.type === 'bearer' || auth?.type === 'basic')) {
    return 'authorization carries the endpoint credential; change it under authentication';
  }
  if (auth?.type === 'api_key' && key === auth.header?.trim().toLowerCase()) {
    return `${key} carries the API key; change it under authentication`;
  }
  return null;
};

export const forwardRefusal = (name: string, auth: IAuthShape | undefined): string | null => {
  const key = name.trim().toLowerCase();
  if (key === 'cookie' || key === 'authorization') {
    return `${key} is the sender's credential to us; it is never forwarded`;
  }
  return reservedHeaderReason(name, auth);
};

export type THeaderOrigin = 'hookpipe' | 'forwarded' | 'static' | 'signature' | 'auth';

export interface IReceivedHeader {
  name: string;
  value: string;
  origin: THeaderOrigin;
}

interface IReceivedInput {
  staticHeaders: { key: string; value: string }[];
  forwardHeaders: string[];
  signing: boolean;
  auth: IAuthShape & { username?: string; prefix?: string };
  authReady: boolean;
}

/** What a delivery carries, in the order the transport writes it (`buildHeaders`). */
export const receivedHeaders = (input: IReceivedInput): IReceivedHeader[] => {
  const rows: IReceivedHeader[] = [
    { name: 'content-type', value: "the sender's, else application/json", origin: 'hookpipe' },
    { name: 'user-agent', value: 'hookpipe/<version>', origin: 'hookpipe' },
    { name: 'idempotency-key', value: 'the event id', origin: 'hookpipe' },
    { name: 'x-hookpipe-event', value: 'the event id', origin: 'hookpipe' },
    { name: 'x-hookpipe-attempt', value: '1, 2, 3…', origin: 'hookpipe' },
    { name: 'x-hookpipe-source', value: 'the source name', origin: 'hookpipe' },
    { name: 'x-hookpipe-event-type', value: 'when the event has one', origin: 'hookpipe' },
    { name: 'x-hookpipe-customer', value: 'when a customer is known', origin: 'hookpipe' },
  ];

  for (const name of input.forwardHeaders) {
    if (name.trim() && forwardRefusal(name, input.auth) === null) {
      rows.push({
        name: name.trim().toLowerCase(),
        value: 'as the sender sent it',
        origin: 'forwarded',
      });
    }
  }
  for (const row of input.staticHeaders) {
    if (row.key.trim() && reservedHeaderReason(row.key, input.auth) === null) {
      rows.push({ name: row.key.trim().toLowerCase(), value: row.value, origin: 'static' });
    }
  }
  if (input.signing) {
    rows.push({ name: SIGNATURE_HEADER, value: 't=<unix>,v1=<hex>', origin: 'signature' });
  }
  if (input.authReady) rows.push(authHeader(input.auth));
  return rows;
};

const authHeader = (auth: IReceivedInput['auth']): IReceivedHeader => {
  if (auth.type === 'api_key') {
    return {
      name: (auth.header ?? 'x-api-key').toLowerCase(),
      value: `${auth.prefix ?? ''}<the API key>`,
      origin: 'auth',
    };
  }
  const value =
    auth.type === 'basic' ? `Basic base64(${auth.username ?? ''}:<password>)` : 'Bearer <token>';
  return { name: 'authorization', value, origin: 'auth' };
};

export const generateSecret = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return `whsec_${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
};

// Kept a plain string: the page shows it in a read-only CodeView and copies it verbatim.
export const VERIFY_SNIPPET = `import { createHmac, timingSafeEqual } from 'node:crypto';

// rawBody: the request body exactly as received (a Buffer), before any JSON parsing.
export function verifyHookpipe(rawBody, header, secret, toleranceSec = 300) {
  const parts = header.split(',').map((part) => part.split('='));
  const t = Number(parts.find(([key]) => key === 't')?.[1]);
  const signatures = parts.filter(([key]) => key === 'v1').map(([, value]) => value);
  if (!Number.isFinite(t) || Math.abs(Date.now() / 1000 - t) > toleranceSec) return false;

  const expected = createHmac('sha256', secret).update(\`\${t}.\`).update(rawBody).digest();
  // During a key rotation there are two v1 values; either one matching is a pass.
  return signatures.some((hex) => {
    const given = Buffer.from(hex, 'hex');
    return given.length === expected.length && timingSafeEqual(given, expected);
  });
}
`;
